/**
 * Section: one titled card on the Home and Records tabs (plan
 * docs/plans/lms-plan.md 7.1, 7.5; the design skill's card recipe: rounded-xl
 * border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800), with
 * a quiet header row (title, an optional count, actions on the right) and the
 * two states every block shares: a skeleton while loading and an error line
 * with Retry.
 *
 * The error line deliberately carries no role="alert": the module shell's
 * identity failure is the page's one alert (WP-03's Layout), and a block that
 * could not load is not an emergency. It is announced politely instead.
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { outlineBtnCls } from '../../../ui/styles';

export const sectionCardCls = 'rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800';

export interface SectionProps {
  title: string;
  icon?: LucideIcon;
  /** Shown after the title as "(n)" (the PageHeader count convention). */
  count?: number | null;
  actions?: ReactNode;
  children: ReactNode;
  'data-testid'?: string;
  /** id for the heading (aria-labelledby on the section). */
  headingId: string;
}

export function Section({ title, icon: Icon, count, actions, children, headingId, 'data-testid': testId }: SectionProps) {
  return (
    <section className={sectionCardCls} aria-labelledby={headingId} data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h2 id={headingId} className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {Icon ? <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" /> : null}
          {title}
          {typeof count === 'number' ? (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">{`(${count.toLocaleString()})`}</span>
          ) : null}
        </h2>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** Skeleton rows while a block loads (the design skill's skeleton recipe). */
export function SectionSkeleton({ rows = 3, 'data-testid': testId }: { rows?: number; 'data-testid'?: string }) {
  return (
    <div className="space-y-3 p-4" aria-busy="true" data-testid={testId}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="space-y-1.5">
          <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

/** A block that could not load: the message and Retry (G10: failures are visible, never a silent empty list). */
export function SectionError({ message, onRetry, 'data-testid': testId }: { message: string; onRetry: () => void; 'data-testid'?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-3 p-4 text-sm text-red-700 dark:text-red-400" aria-live="polite" data-testid={testId}>
      <span>{message}</span>
      <button type="button" className={outlineBtnCls()} onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

export default Section;
